import { addRankBadgeOverheadImageTexture } from "@/game/load-data-texture";
import {
  RANK_BADGE_OVERHEAD_DISPLAY_HEIGHT_PX,
  RANK_BADGE_OVERHEAD_DISPLAY_WIDTH_PX,
  RANK_BADGE_OVERHEAD_MIN_CHARACTER_WIDTH_RATIO,
  type LeaderboardBoard,
  type RankBadgeConfigBoard,
} from "@/rank/leaderboard-types";
import type Phaser from "phaser";

const BOARD_ORDER: LeaderboardBoard[] = ["raid", "tower", "wealth"];
const BADGE_GAP_PX = 3;
const HEAD_GAP_PX = 4;

type BadgeStackItem = {
  sprite: Phaser.GameObjects.Sprite;
  displayH: number;
};

function texKey(board: LeaderboardBoard): string {
  return `rank-badge-tex-${board}`;
}

function resolveBadgeDisplaySize(follow: Phaser.GameObjects.Sprite): { w: number; h: number } {
  let w = RANK_BADGE_OVERHEAD_DISPLAY_WIDTH_PX;
  let h = RANK_BADGE_OVERHEAD_DISPLAY_HEIGHT_PX;
  const minW = Math.ceil(follow.displayWidth * RANK_BADGE_OVERHEAD_MIN_CHARACTER_WIDTH_RATIO);
  if (w < minW) {
    const scale = minW / w;
    w = Math.round(w * scale);
    h = Math.round(h * scale);
  }
  return { w, h };
}

export class RankBadgeOverhead {
  private items: BadgeStackItem[] = [];

  private constructor(
    private readonly follow: Phaser.GameObjects.Sprite,
    items: BadgeStackItem[],
  ) {
    this.items = items;
  }

  static async create(
    scene: Phaser.Scene,
    follow: Phaser.GameObjects.Sprite,
    activeBoards: LeaderboardBoard[],
    configList: RankBadgeConfigBoard[],
  ): Promise<RankBadgeOverhead | null> {
    const configs = new Map<LeaderboardBoard, RankBadgeConfigBoard>();
    for (const c of configList) configs.set(c.board, c);
    const boards = BOARD_ORDER.filter((b) => activeBoards.includes(b));
    const items: BadgeStackItem[] = [];
    const { w, h } = resolveBadgeDisplaySize(follow);

    for (const board of boards) {
      const cfg = configs.get(board);
      const url = cfg?.spritesheetDataUrl;
      if (!url?.startsWith("data:")) continue;
      const key = texKey(board);
      try {
        await addRankBadgeOverheadImageTexture(scene.textures, key, url);
      } catch {
        continue;
      }
      const sprite = scene.add.sprite(0, 0, key);
      sprite.setOrigin(0.5, 1);
      sprite.setDisplaySize(w, h);
      items.push({ sprite, displayH: h });
    }

    if (items.length === 0) return null;
    const overhead = new RankBadgeOverhead(follow, items);
    overhead.sync();
    return overhead;
  }

  sync(): void {
    if (!this.follow.active) return;
    const visible = this.follow.visible;
    for (const item of this.items) item.sprite.setVisible(visible);
    if (!visible) return;

    const depth = this.follow.depth + 2;
    const centerX = Math.round(this.follow.x);
    let y = Math.round(this.follow.y - this.follow.displayHeight - HEAD_GAP_PX);
    for (const item of this.items) {
      item.sprite.setPosition(centerX, y);
      item.sprite.setDepth(depth);
      y -= item.displayH + BADGE_GAP_PX;
    }
  }

  destroy(): void {
    for (const item of this.items) item.sprite.destroy();
    this.items = [];
  }
}

export function attachPlayerRankBadges(
  scene: Phaser.Scene,
  follow: Phaser.GameObjects.Sprite,
): Promise<RankBadgeOverhead | null> {
  const badgeBoards =
    (scene.registry.get("rankBadgeBoards") as LeaderboardBoard[] | undefined) ?? [];
  const badgeConfigs =
    (scene.registry.get("rankBadgeConfigs") as RankBadgeConfigBoard[] | undefined) ?? [];
  if (!badgeBoards.length) return Promise.resolve(null);
  return RankBadgeOverhead.create(scene, follow, badgeBoards, badgeConfigs);
}
